/**
 * useSimOrderBridge – SSE → OrderStore bridge
 *
 * Connects to the simulation SSE stream and dispatches events into the
 * OrderStore context (register shifts, sessions, orders, payments,
 * table close, shift close, sim cleanup).
 *
 * Mounted at the SimulatorPanel level so it stays alive regardless of
 * which sub-tab (TrafficSim vs TableOps) is active. This guarantees
 * that KDS tickets populate in real time even when the user is watching
 * the TrafficSim event feed.
 *
 * On mount it also performs a one-shot hydration via getSimState()
 * to catch up with events that fired before the component existed.
 */

import { useEffect, useRef } from 'react'
import { connectSimStream, getSimState, getSimEvents, type SimEvent } from './api/simulationApi'
import { useOrderStore } from './useOrderStore'
import { dispatchOrder, deriveOrderStatus, type TicketStatus, type StationId } from './orderTypes'
import { shouldProcessSimEvent } from './simEventTenantFilter'

export function useSimOrderBridge(
  activeTenantId: number | null,
  opts: { allowRegisterAutoOpen?: boolean } = {},
) {
  const { allowRegisterAutoOpen = false } = opts
  const { dispatch: storeDispatch, state: storeState } = useOrderStore()

  // Ref tracking whether a register is open — avoids stale closure
  const registerOpenRef = useRef(!!storeState.register)
  registerOpenRef.current = !!storeState.register

  // Synchronous flag: tracks whether we already dispatched OPEN_REGISTER
  // within this SSE batch (before React re-renders and updates registerOpenRef).
  const didAutoOpenRef = useRef(false)
  if (storeState.register) didAutoOpenRef.current = true
  if (!storeState.register) didAutoOpenRef.current = false

  useEffect(() => {
    let cleanup: (() => void) | null = null
    let cancelled = false

    // Strict tenant gate: do nothing until a tenant is selected in the FE.
    // Without this, the bridge connects the SSE stream during the brief
    // initial-render window where `activeTenantId` is null and dispatches
    // cross-tenant events into the wrong OrderStore.
    if (activeTenantId == null) return

    try {
      const conn = connectSimStream
      if (typeof conn === 'function') {
        cleanup = conn((evt: SimEvent) => {
          // Defence-in-depth: drop any event that doesn't belong to the
          // currently-selected tenant (see simEventTenantFilter.ts).
          if (!shouldProcessSimEvent(evt as { tenantId?: number | string }, activeTenantId)) return
          try {
            if (evt.event === 'shift_opened') {
              if (allowRegisterAutoOpen && !registerOpenRef.current && !didAutoOpenRef.current) {
                didAutoOpenRef.current = true
                storeDispatch({ type: 'OPEN_REGISTER', payload: { openingFloat: 0 } })
              }

            } else if (evt.event === 'session_opened') {
              // Auto-open register on first session if not already open.
              // The reducer is idempotent, so duplicate dispatches are harmless.
              if (allowRegisterAutoOpen && !registerOpenRef.current && !didAutoOpenRef.current) {
                didAutoOpenRef.current = true
                storeDispatch({ type: 'OPEN_REGISTER', payload: { openingFloat: 0 } })
              }
              storeDispatch({
                type: 'OPEN_SESSION',
                payload: {
                  session_key: String(evt.sessionKey || evt.session_key || ''),
                  table_key: String(evt.tableKey || evt.table_key || ''),
                  opened_at: String(evt.ts || new Date().toISOString()),
                  checks: Number(evt.checks || evt.checks_per_table || 1),
                  covers: Number(evt.covers || 0),
                },
              })

            } else if (evt.event === 'order_placed') {
              const tableKey = String(evt.tableKey || evt.table_key || '')
              const rawLines = evt.orderLines as Array<Record<string, unknown>> | undefined
              if (tableKey && Array.isArray(rawLines) && rawLines.length > 0) {
                const lines = rawLines.map((l, idx) => ({
                  id: idx + 1,
                  scopeKey: String(l.item_key || ''),
                  name: String(l.name || ''),
                  description: String(l.description || ''),
                  price: Number(l.price || 0),
                  quantity: Number(l.quantity || 1),
                  section: String(l.section || ''),
                  station: (l.station as StationId | undefined),
                }))
                const byCheck: Record<number, typeof lines> = {}
                rawLines.forEach((l, idx) => {
                  const ci = Number(l.check_index || 0)
                  if (!byCheck[ci]) byCheck[ci] = []
                  byCheck[ci].push(lines[idx])
                })
                Object.entries(byCheck).forEach(([ci, checkLines]) => {
                  storeDispatch({
                    type: 'DISPATCH',
                    payload: { scopeId: tableKey, checkIndex: Number(ci), lines: checkLines },
                  })
                })
              }

            } else if (evt.event === 'order_ready') {
              // Kitchen finished preparing — bump in_progress → ready
              const tableKey = String(evt.tableKey || evt.table_key || '')
              if (tableKey) {
                storeDispatch({
                  type: 'BUMP_TABLE_TICKETS',
                  payload: { scopeId: tableKey, fromStatus: 'in_progress', toStatus: 'ready' },
                })
              }

            } else if (evt.event === 'order_delivered') {
              // Waitress brought food to table — bump ready → delivered
              const tableKey = String(evt.tableKey || evt.table_key || '')
              if (tableKey) {
                storeDispatch({
                  type: 'BUMP_TABLE_TICKETS',
                  payload: { scopeId: tableKey, fromStatus: 'ready', toStatus: 'delivered' },
                })
              }

            } else if (evt.event === 'table_closed') {
              const scopeId = String(evt.tableKey || evt.table_key || '')
              const checks = Number(evt.checks || 1)
              const subtotal = Number(evt.subtotal || 0)
              const tip = Number(evt.tip || 0)
              const paymentMethod = String(evt.paymentMethod || 'cash')
              const perCheck = checks > 0 ? Math.round((subtotal / checks) * 100) / 100 : subtotal
              const tipPerCheck = checks > 0 ? Math.round((tip / checks) * 100) / 100 : tip
              for (let ci = 0; ci < checks; ci++) {
                storeDispatch({
                  type: 'PAY_CHECK',
                  payload: {
                    checkIndex: ci,
                    scopeId,
                    paymentMethodId: paymentMethod,
                    amount: perCheck,
                    tip: tipPerCheck,
                    paid_at: String(evt.ts || new Date().toISOString()),
                  },
                })
              }
              storeDispatch({ type: 'CLOSE_SESSION', payload: { scopeId } })
              storeDispatch({ type: 'CLOSE_TABLE', payload: { scopeId } })

            } else if (evt.event === 'shift_closed') {
              // Respect mode flag: in operator/manual cashier flows we should not
              // let simulation lifecycle events force-close the register UI.
              if (allowRegisterAutoOpen && registerOpenRef.current) {
                storeDispatch({ type: 'CLOSE_REGISTER' })
                // Synchronously reflect the closed state so that a shift_opened
                // event arriving in the same SSE batch (same tick) sees the
                // register as closed and can re-open it.  Without this, the ref
                // stays true until React re-renders, and the subsequent
                // shift_opened guard (!registerOpenRef.current) fails — leaving
                // the cashier visually closed for the entire new shift.
                registerOpenRef.current = false
                didAutoOpenRef.current = false   // allow re-auto-open for next shift
              }

            } else if (evt.event === 'guest_left') {
              // Safety net: close session when the table is freed.
              // Normally table_closed handles this, but error-path removals
              // skip _doPayAndClose and only emit guest_left — leaving a
              // stale session in the store that desynchronises the floor plan.
              if ((evt as any).tableFreed) {
                const scopeId = String((evt as any).tableKey || (evt as any).table_key || '')
                if (scopeId) {
                  storeDispatch({ type: 'CLOSE_SESSION', payload: { scopeId } })
                }
              }

            } else if (evt.event === 'stopped' || evt.event === 'sim_cleanup') {
              storeDispatch({ type: 'RESET_STATE' })
              // Synchronously clear BOTH refs so subsequent shift_opened
              // in the same SSE batch (or before React re-renders) sees
              // the register as closed and can re-open it.
              registerOpenRef.current = false
              didAutoOpenRef.current = false
            }
          } catch {
            // non-fatal — keep UI stable
          }
        }, () => {}, activeTenantId != null ? { tenantId: activeTenantId } : undefined)
      }
    } catch {
      // ignore — sim stream may not be available in this context
    }

    // ── Hydrate on mount: catch up with events that fired before bridge existed ──
    ;(async () => {
      try {
        // Strict tenant isolation: never hydrate without a selected tenant.
        // Without this guard, the initial render (tenants list still loading)
        // would pull in sim state from whatever tenant the sim is running for
        // and write phantom sessions into the wrong OrderStore.
        if (activeTenantId == null) return
        const simState = await getSimState()
        if (cancelled || !simState.running) return
        if (simState.tenantId == null) return
        if (String(simState.tenantId) !== String(activeTenantId)) return

        // Hydrate register if sim has open shifts
        const openShifts = (simState.shifts?.activeShifts ?? []).filter(
          (s: { status?: string }) => s.status === 'open',
        )
        if (allowRegisterAutoOpen && openShifts.length > 0 && !registerOpenRef.current) {
          storeDispatch({ type: 'OPEN_REGISTER', payload: { openingFloat: (openShifts[0] as any).openingFloat || 0 } })
        }

        const occupiedKeys = simState.tables?.occupiedKeys ?? []
        const tableGuests  = simState.tables?.tableGuests ?? {}

        // Fetch ring buffer events BEFORE hydrating sessions so we can
        // extract the real session_opened timestamps instead of stamping "now".
        let ringEvents: SimEvent[] = []
        if (occupiedKeys.length > 0) {
          try {
            const res = await getSimEvents(2000)
            ringEvents = res.events ?? []
          } catch { /* non-fatal */ }
        }

        // Build a map of tableKey → real opened_at timestamp from session_opened events
        // Use the LAST (most recent) session_opened per table — covers table re-seats.
        const sessionOpenedTs: Record<string, string> = {}
        for (const evt of ringEvents) {
          if (evt.event !== 'session_opened') continue
          const tk = String((evt as any).tableKey || (evt as any).table_key || '')
          if (tk) sessionOpenedTs[tk] = String((evt as any).ts || '')
        }

        // Hydrate sessions for occupied tables — use real timestamp from engine
        for (const tableKey of occupiedKeys as string[]) {
          const guest = (tableGuests as Record<string, any>)[tableKey]
          storeDispatch({
            type: 'OPEN_SESSION',
            payload: {
              session_key: guest?.sessionKey || `hydrated-${tableKey}`,
              table_key:   tableKey,
              opened_at:   sessionOpenedTs[tableKey] || new Date().toISOString(),
              checks:      guest?.checks || 1,
              covers:      guest?.covers || 1,
            },
          })
        }

        // ── Hydrate orders from sim ring buffer ──
        // Replay order_placed / order_ready / order_delivered events so tables
        // that were populated before the FE loaded show their full order history.
        if (occupiedKeys.length > 0 && ringEvents.length > 0) {
          try {
            const occupiedSet = new Set(occupiedKeys as string[])

            // Build orders per table from order_placed events (oldest first)
            const ordersMap = new Map<string, ReturnType<typeof dispatchOrder>[]>()
            const sortedEvents = [...ringEvents].reverse() // oldest first (ring buffer is newest-first)

            for (const evt of sortedEvents) {
              if (evt.event !== 'order_placed') continue
              const tableKey = String((evt as any).tableKey || (evt as any).table_key || '')
              if (!tableKey || !occupiedSet.has(tableKey)) continue

              const rawLines = (evt as any).orderLines as Array<Record<string, unknown>> | undefined
              if (!Array.isArray(rawLines) || !rawLines.length) continue

              const lines = rawLines.map((l, idx) => ({
                id: idx + 1,
                scopeKey: String(l.item_key || ''),
                name: String(l.name || ''),
                description: String(l.description || ''),
                price: Number(l.price || 0),
                quantity: Number(l.quantity || 1),
                section: String(l.section || ''),
                station: (l.station as StationId | undefined),
              }))

              // Build orders per check (same logic as the SSE handler)
              const byCheck: Record<number, typeof lines> = {}
              rawLines.forEach((l, idx) => {
                const ci = Number(l.check_index || 0)
                if (!byCheck[ci]) byCheck[ci] = []
                byCheck[ci].push(lines[idx])
              })

              if (!ordersMap.has(tableKey)) ordersMap.set(tableKey, [])
              const tableOrders = ordersMap.get(tableKey)!
              for (const [ci, checkLines] of Object.entries(byCheck)) {
                tableOrders.push(dispatchOrder(tableKey, checkLines, Number(ci)))
              }
            }

            // Apply status bumps from order_ready and order_delivered events
            for (const evt of sortedEvents) {
              const tableKey = String((evt as any).tableKey || (evt as any).table_key || '')
              if (!tableKey || !ordersMap.has(tableKey)) continue
              const tableOrders = ordersMap.get(tableKey)!

              let fromStatus: TicketStatus | null = null
              let toStatus: TicketStatus | null = null
              if (evt.event === 'order_ready') {
                fromStatus = 'in_progress'; toStatus = 'ready'
              } else if (evt.event === 'order_delivered') {
                fromStatus = 'ready'; toStatus = 'delivered'
              } else {
                continue
              }

              const now = new Date().toISOString()
              for (const order of tableOrders) {
                for (const ticket of order.tickets) {
                  if (ticket.status === fromStatus) {
                    ticket.status = toStatus
                    ticket.updated_at = now
                    if (toStatus === 'ready') ticket.bumped_at = now
                  }
                }
                order.status = deriveOrderStatus(order.tickets)
              }
            }

            // Dispatch hydrated orders into the store (newest first)
            ordersMap.forEach((orders, tableKey) => {
              if (orders.length > 0) {
                storeDispatch({
                  type: 'HYDRATE_TABLE_ORDERS',
                  payload: { scopeId: tableKey, orders: orders.reverse() },
                })
              }
            })
          } catch {
            // non-fatal — ring buffer might be empty or unavailable
          }
        }
      } catch {
        // non-fatal — SSE will deliver subsequent events
      }
    })()

    return () => { cancelled = true; try { cleanup?.() } catch {} }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeDispatch, activeTenantId, allowRegisterAutoOpen])
}
